<template>
  <div class="p-6 space-y-6">
    <div class="rounded-2xl bg-gradient-to-r from-cyan-50 via-sky-50 to-indigo-50 border border-cyan-100 p-6 shadow-sm">
      <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div class="space-y-1">
          <h1 class="text-3xl font-bold text-gray-900">Dashboard Plan Artritis Reumatoidea</h1>
          <p class="text-gray-600">Estado actual y KPIs del plan</p>
          <div class="flex flex-wrap gap-3 text-sm text-gray-700">
            <span class="inline-flex items-center rounded-full bg-white/70 px-3 py-1 border border-cyan-100 shadow-sm">
              <span class="font-semibold text-cyan-700">{{ formatNumber(totalesResumidos.afiliadosTotales) }}</span>
              <span class="ml-2">afiliados totales</span>
            </span>
            <span class="inline-flex items-center rounded-full bg-white/70 px-3 py-1 border border-cyan-100 shadow-sm">
              <span class="font-semibold text-cyan-700">{{ formatNumber(totalesResumidos.afiliadosAnuales) }}</span>
              <span class="ml-2">afiliados activos en el año</span>
            </span>
            <span class="inline-flex items-center rounded-full bg-white/70 px-3 py-1 border border-cyan-100 shadow-sm">
              <span class="font-semibold text-cyan-700">{{ formatNumber(totalesResumidos.practicas) }}</span>
              <span class="ml-2">practicas en el año</span>
            </span>
            <span class="inline-flex items-center rounded-full bg-white/70 px-3 py-1 border border-cyan-100 shadow-sm">
              <span class="font-semibold text-cyan-700">{{ formatCurrency(totalesResumidos.gastosTotales) }}</span>
              <span class="ml-2">gasto total</span>
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <label for="Añoo" class="text-sm font-medium text-gray-700">Año</label>
          <select
            id="anio"
            v-model="selectedYear"
            @change="onYearChange"
            class="block min-w-[120px] rounded-lg border-gray-300 bg-white px-3 pr-8 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="errorMessage" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
      {{ errorMessage }}
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12 text-gray-600">
      <span class="h-5 w-5 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent"></span>
      <span class="ml-3 text-sm font-medium">Cargando informacion...</span>
    </div>

    <div v-else class="space-y-6">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          v-for="card in kpiCards"
          :key="card.key"
          :titulo="card.title"
          :valor-real="card.value"
          :meta="card.meta"
          :tipo="card.type"
          :direccion="card.direction"
          :descripcion="card.description"
        />
      </div>

      <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div class="col-span-2 rounded-xl border border-gray-100 bg-white p-4 shadow-md">
          <div class="mb-4 flex items-start justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Evolucion mensual del gasto</h3>
              <p class="text-sm text-gray-500">Gasto total del plan</p>
            </div>
            <div class="flex items-center gap-2 text-xs text-gray-600">
              <span class="hidden sm:inline">Vista:</span>
              <button
                type="button"
                @click="gastoView = 'mensual'"
                :class="['rounded-lg border px-2 py-1 font-semibold', gastoView === 'mensual' ? 'border-cyan-500 text-cyan-700 bg-cyan-50' : 'border-gray-200 text-gray-600']"
              >
                Mensual
              </button>
              <button
                type="button"
                @click="gastoView = 'trimestral'"
                :class="['rounded-lg border px-2 py-1 font-semibold', gastoView === 'trimestral' ? 'border-cyan-500 text-cyan-700 bg-cyan-50' : 'border-gray-200 text-gray-600']"
              >
                Trimestral
              </button>
            </div>
            <span v-if="loadingSeries" class="h-4 w-4 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent"></span>
          </div>
          <div class="relative h-72">
            <canvas ref="lineChartRef"></canvas>
            <div
              v-if="!hasGastoData"
              class="absolute inset-0 flex items-center justify-center bg-white/80 text-sm font-medium text-gray-500"
            >
              Sin datos
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-md">
          <div class="mb-4 flex items-start justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Consultas por especialidad</h3>
              <p class="text-sm text-gray-500">Distribucion de atenciones</p>
            </div>
            <span v-if="loadingSeries" class="h-4 w-4 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent"></span>
          </div>
          <div class="relative h-72">
            <canvas ref="barChartRef"></canvas>
            <div
              v-if="!hasConsultasData"
              class="absolute inset-0 flex items-center justify-center bg-white/80 text-sm font-medium text-gray-500"
            >
              Sin datos
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-md">
          <div class="mb-3 flex items-center justify-between">
            <div>
              <h4 class="text-md font-semibold text-gray-900">Distribucion por edad</h4>
              <p class="text-xs text-gray-500">Rangos etarios</p>
            </div>
          </div>
          <ul class="space-y-2 text-sm">
            <li v-for="(item, idx) in distRangos" :key="item.clave || idx" class="flex justify-between text-gray-800">
              <span>{{ item.clave || 'N/D' }}</span>
              <span class="font-semibold">{{ item.cantidad ?? 0 }}</span>
            </li>
            <li v-if="!distRangos.length" class="text-gray-500 text-sm">Sin datos</li>
          </ul>
        </div>
        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-md">
          <div class="mb-3 flex items-center justify-between">
            <div>
              <h4 class="text-md font-semibold text-gray-900">Distribucion por genero</h4>
              <p class="text-xs text-gray-500">Relevamiento actual</p>
            </div>
          </div>
          <ul class="space-y-2 text-sm">
            <li v-for="(item, idx) in distGenero" :key="item.clave || idx" class="flex justify-between text-gray-800">
              <span>{{ item.clave || 'N/D' }}</span>
              <span class="font-semibold">{{ item.cantidad ?? 0 }}</span>
            </li>
            <li v-if="!distGenero.length" class="text-gray-500 text-sm">Sin datos</li>
          </ul>
        </div>
        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-md">
          <div class="mb-3 flex items-center justify-between">
            <div>
              <h4 class="text-md font-semibold text-gray-900">Distribucion por delegacion</h4>
              <p class="text-xs text-gray-500">Cantidad de afiliados</p>
            </div>
          </div>
          <ul class="space-y-2 text-sm max-h-60 overflow-y-auto pr-1">
            <li v-for="(item, idx) in distDelegaciones" :key="item.clave || idx" class="flex justify-between text-gray-800">
              <span class="truncate">{{ item.clave || 'N/D' }}</span>
              <span class="font-semibold">{{ item.cantidad ?? 0 }}</span>
            </li>
            <li v-if="!distDelegaciones.length" class="text-gray-500 text-sm">Sin datos</li>
          </ul>
        </div>
      </div>

      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-md">
        <div class="mb-4 flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Top 10 afiliados de alto consumo</h3>
            <p class="text-sm text-gray-500">Ordenados por costo anual</p>
          </div>
          <span v-if="loadingSeries" class="h-4 w-4 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent"></span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-2 text-left font-semibold text-gray-600">Nombre</th>
                <th class="px-4 py-2 text-left font-semibold text-gray-600">Nro Afiliado</th>
                <th class="px-4 py-2 text-right font-semibold text-gray-600">Costo anual</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, index) in topAfiliados" :key="row.idAfiliado || row.nroAfiliado || index" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-gray-900">{{ row.nombre || 'Sin nombre' }}</td>
                <td class="px-4 py-3 text-gray-700">{{ row.nroAfiliado || row.idAfiliado || '-' }}</td>
                <td class="px-4 py-3 text-right font-semibold text-gray-900">
                  {{ formatCurrency(row.costoAnual ?? row.costo ?? 0) }}
                </td>
              </tr>
              <tr v-if="!topAfiliados.length">
                <td colspan="3" class="px-4 py-4 text-center text-gray-500">Sin datos para mostrar</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import KpiCard from '../components/cards/KpiCard.vue';
import { defaultMetasAr, fetchMetasAr } from '../services/metasArService';

export default {
  name: 'DashboardArtritis',
  components: { KpiCard },
  setup() {
    const currentYear = new Date().getFullYear();
    const loadStoredYear = () => currentYear;
    const buildYearOptions = () => {
      const years = [];
      for (let i = 0; i <= 5; i += 1) {
        years.push(currentYear - i);
      }
      return years;
    };
    const loading = ref(true);
    const loadingSeries = ref(false);
    const errorMessage = ref('');
    const selectedYear = ref(loadStoredYear());
    const yearOptions = ref(Array.from(new Set([...buildYearOptions(), selectedYear.value])).sort((a, b) => b - a));
    const gastoView = ref('mensual'); // mensual | trimestral

    const kpis = ref({});
    const totales = ref({});
    const metas = ref(defaultMetasAr);
    const series = ref({
      gastoMensual: [],
      consultasPorEspecialidad: [],
      topAfiliadosAltoConsumo: [],
    });
    const distribucion = ref({
      rangosEtarios: [],
      genero: [],
      delegaciones: [],
    });

    const lineChartRef = ref(null);
    const barChartRef = ref(null);
    let lineChartInstance = null;
    let barChartInstance = null;
    let ChartJs = null;

    const currencyFormatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    });
    const numberFormatter = new Intl.NumberFormat('es-AR');

    const formatCurrency = (value) => {
      if (value === null || value === undefined || isNaN(value)) return '$ 0';
      return currencyFormatter.format(Number(value));
    };
    const formatNumber = (value) => {
      if (value === null || value === undefined || isNaN(value)) return '0';
      return numberFormatter.format(Number(value));
    };

    const toArray = (value) => (Array.isArray(value) ? value : []);
    const normalizeBonos = (value) => {
      if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
      if (value === null || value === undefined || value === '') return [];
      return [String(value)];
    };

    const parseMonthNumber = (value) => {
      if (value === null || value === undefined) return null;
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 12) return numeric;
      const text = String(value).toLowerCase();
      const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
      const idx = months.findIndex((m) => text.includes(m));
      return idx >= 0 ? idx + 1 : null;
    };

    const kpiCards = computed(() => {
      const data = kpis.value || {};
      const metaValues = metas.value || {};

      const getCostoAnualPromedio = () => {
        const candidateKeys = [
          'costoAnualPromedioPorAfiliado',
          'costoAnualPromedio',
          'costoPromedio',
          'costoAnual',
          'costo',
        ];
        for (const key of candidateKeys) {
          const val = data[key];
          if (val !== undefined && val !== null && !Number.isNaN(Number(val))) {
            return val;
          }
        }
        // Si no encontramos, devuelve null para mostrar "--"
        console.warn('KPI costoAnualPromedio no encontrado en respuesta de KPIs', data);
        return null;
      };

      return [
        {
          key: 'adherencia',
          title: 'Adherencia',
          description: 'Porcentaje de pacientes que cumplen con controles trimestrales.',
          value: data.adherenciaControlesTrimestrales,
          meta: metaValues.metaAdherencia,
          type: 'percent',
          direction: 'higher',
        },
        {
          key: 'calidadClinica',
          title: 'Calidad clinica',
          description: 'Pacientes con DAS28 < 3.2 (remision/baja actividad).',
          value: data.calidadClinicaDas28Menor32,
          meta: metaValues.metaCalidadClinica,
          type: 'percent',
          direction: 'higher',
        },
        {
          key: 'eficienciaEconomica',
          title: 'Costo anual promedio',
          description: 'Costo anual promedio del afiliado en el plan de artritis.',
          value: getCostoAnualPromedio(),
          meta: null,
          type: 'currency',
          direction: 'lower',
        },
        {
          key: 'auditoriaDigital',
          title: 'Auditoria digital',
          description: 'Porcentaje de auditorias realizadas con IA/digitales.',
          value: data.porcentajeAuditoriasIA,
          meta: metaValues.metaAuditoriaDigital,
          type: 'percent',
          direction: 'higher',
        },
        {
          key: 'seguimientoContinuo',
          title: 'Seguimiento continuo',
          description: 'Pacientes sin control en los ultimos 6 meses.',
          value: data.porcentajePacientesSinControl6Meses,
          meta: metaValues.metaSeguimiento6m,
          type: 'percent',
          direction: 'lower',
        },
      ];
    });

    const gastoMensualRaw = computed(() =>
      toArray(series.value?.gastoMensual).map((item, idx) => {
        const label = item.mes ?? item.label ?? item.periodo ?? `Mes ${idx + 1}`;
        const month =
          parseMonthNumber(item.mes ?? item.numeroMes ?? item.month ?? item.periodo ?? label) ?? idx + 1;
        const value = Number(item.monto ?? item.valor ?? item.total ?? item.costo ?? 0);
        const afiliado = item.nombreAfiliado ?? item.afiliado ?? item.nombre ?? '';
        const bonos = normalizeBonos(item.numeroBono ?? item.numeroBonos ?? item.bonos ?? item.bono);
        return { label, month, value, afiliado, bonos };
      })
    );

    const gastoMensualAggregated = computed(() => {
      const sums = [0, 0, 0, 0];
      const seen = [false, false, false, false];
      const afiliadosSet = [new Set(), new Set(), new Set(), new Set()];
      const bonosSet = [new Set(), new Set(), new Set(), new Set()];

      gastoMensualRaw.value.forEach((item) => {
        if (!item.month || item.month < 1 || item.month > 12) return;
        const q = Math.ceil(item.month / 3) - 1;
        sums[q] += item.value;
        seen[q] = true;
        if (item.afiliado) afiliadosSet[q].add(item.afiliado);
        if (Array.isArray(item.bonos)) {
          item.bonos.forEach((bono) => {
            if (bono) bonosSet[q].add(bono);
          });
        }
      });

      const labels = [];
      const data = [];
      const details = [];
      sums.forEach((sum, idx) => {
        if (seen[idx] || sum !== 0) {
          labels.push(`T${idx + 1}`);
          data.push(sum);
          details.push({
            afiliados: Array.from(afiliadosSet[idx]),
            bonos: Array.from(bonosSet[idx]),
          });
        }
      });
      // Si no hay meses reconocidos, devolvemos listas vacias
      return { labels, data, details };
    });

    const gastoMensualLabels = computed(() => {
      if (gastoView.value === 'trimestral') return gastoMensualAggregated.value.labels;
      return gastoMensualRaw.value.map((item) => item.label || '');
    });
    const gastoMensualData = computed(() => {
      if (gastoView.value === 'trimestral') return gastoMensualAggregated.value.data;
      return gastoMensualRaw.value.map((item) => Number(item.value ?? 0));
    });
    const buildDetalleTooltip = (detalle) => {
      const afiliadosList = Array.isArray(detalle?.afiliados)
        ? detalle.afiliados.filter(Boolean)
        : detalle?.afiliado
          ? [detalle.afiliado]
          : [];
      const bonosList = Array.isArray(detalle?.bonos) ? detalle.bonos.filter(Boolean) : [];
      const afiliadosLabel = afiliadosList.length > 1 ? 'Afiliados' : 'Afiliado';
      const afiliados = afiliadosList.length ? afiliadosList.join(', ') : 'Sin datos';
      const bonos = bonosList.length ? bonosList.join(', ') : 'Sin datos';
      return [` ${afiliadosLabel}: ${afiliados}`, ` Bonos: ${bonos}`];
    };

    const consultasLabels = computed(() =>
      toArray(series.value?.consultasPorEspecialidad).map((item) => item.especialidad || item.nombre || "")
    );
    const consultasData = computed(() =>
      toArray(series.value?.consultasPorEspecialidad).map((item) => Number(item.consultas ?? item.cantidad ?? item.valor ?? 0))
    );

    const hasGastoData = computed(() => gastoMensualData.value.some((v) => Number.isFinite(v) && v !== 0) || gastoMensualData.value.length > 0);
    const hasConsultasData = computed(() => consultasData.value.some((v) => Number.isFinite(v) && v !== 0) || consultasData.value.length > 0);

    const topAfiliados = computed(() => toArray(series.value?.topAfiliadosAltoConsumo));
    const distRangos = computed(() => toArray(distribucion.value?.rangosEtarios));
    const distGenero = computed(() => toArray(distribucion.value?.genero));
    const distDelegaciones = computed(() => toArray(distribucion.value?.delegaciones));
    const totalesResumidos = computed(() => ({
      afiliadosTotales: Number(totales.value?.CantidadTotalAfiliados) || 0,
      afiliadosAnuales: Number(totales.value?.CantidadAfiliadosAnuales) || 0,
      practicas: Number(totales.value?.CantidadPracticas) || 0,
      gastosTotales: Number(totales.value?.GastosTotales) || 0,
    }));

    const fetchKpis = async (year) => {
      try {
        const { data } = await axios.get('/artritis-dashboard/kpis', {
          params: { anio: year },
        });
        kpis.value = data || {};
      } catch (error) {
        console.error('Error KPIs', error);
        throw new Error('No se pudieron calcular los KPIs.');
      }
    };

    const fetchTotales = async (year) => {
      const { data } = await axios.get('/artritis-dashboard/totales', {
        params: { anio: year },
      });
      totales.value = data || {};
    };

    const fetchSeries = async (year) => {
      loadingSeries.value = true;
      try {
        const { data } = await axios.get('/artritis-dashboard/series', {
          params: { anio: year },
        });
        series.value = data || { gastoMensual: [], consultasPorEspecialidad: [], topAfiliadosAltoConsumo: [] };
      } finally {
        loadingSeries.value = false;
      }
    };

    const fetchDistribucion = async (year) => {
      try {
        const { data } = await axios.get('/artritis-dashboard/distribucion-demografica', {
          params: { anio: year },
        });
        distribucion.value = data || { rangosEtarios: [], genero: [], delegaciones: [] };
      } catch (error) {
        console.error('Error distribucion demografica', error);
        throw new Error('No se pudo obtener la distribucion demografica.');
      }
    };

    const fetchMetas = async (year) => {
      try {
        metas.value = await fetchMetasAr(year);
      } catch (error) {
        console.error('Error cargando metas AR', error);
        metas.value = defaultMetasAr;
      }
    };

    const loadData = async (year = selectedYear.value) => {
      loading.value = true;
      errorMessage.value = '';
      const safeYear = Number.isFinite(Number(year)) && Number(year) > 0 ? Number(year) : currentYear;
      selectedYear.value = safeYear;
      localStorage.setItem('artritis-dashboard-year', safeYear);
      try {
        await Promise.all([
          fetchKpis(safeYear),
          fetchSeries(safeYear),
          fetchDistribucion(safeYear),
          fetchMetas(safeYear),
          fetchTotales(safeYear),
        ]);
      } catch (error) {
        console.error('Error cargando dashboard de artritis', error);
        errorMessage.value = error?.message || 'No pudimos cargar los datos del dashboard. Intente nuevamente.';
      } finally {
        loading.value = false;
      }
    };

    const onYearChange = async () => {
      await loadData(selectedYear.value);
    };

    const loadChartJs = () => new Promise((resolve, reject) => {
      if (ChartJs) return resolve(ChartJs);
      if (window.Chart) {
        ChartJs = window.Chart;
        return resolve(ChartJs);
      }
      const scriptId = 'chartjs-cdn';
      if (document.getElementById(scriptId)) {
        const checkInterval = setInterval(() => {
          if (window.Chart) {
            clearInterval(checkInterval);
            ChartJs = window.Chart;
            resolve(ChartJs);
          }
        }, 50);
        return;
      }
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.3/dist/chart.umd.min.js';
      script.async = true;
      script.onload = () => {
        ChartJs = window.Chart;
        resolve(ChartJs);
      };
      script.onerror = () => reject(new Error('No se pudo cargar Chart.js'));
      document.head.appendChild(script);
    });

    const destroyCharts = () => {
      if (lineChartInstance) {
        lineChartInstance.destroy();
        lineChartInstance = null;
      }
      if (barChartInstance) {
        barChartInstance.destroy();
        barChartInstance = null;
      }
    };

    const renderLineChart = async () => {
      const ctx = lineChartRef.value?.getContext('2d');
      if (!ctx) return;
      const Chart = await loadChartJs();
      if (lineChartInstance) lineChartInstance.destroy();

      lineChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: gastoMensualLabels.value,
          datasets: [
            {
              label: 'Gasto total',
              data: gastoMensualData.value,
              borderColor: '#0ea5e9',
              backgroundColor: 'rgba(14, 165, 233, 0.15)',
              tension: 0.3,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (ctx) => {
                  const baseLine = ` Gasto: ${formatCurrency(ctx.parsed.y || 0)}`;
                  if (gastoView.value === 'mensual') {
                    const detail = gastoMensualRaw.value[ctx.dataIndex];
                    if (!detail) return baseLine;
                    return [baseLine, ...buildDetalleTooltip(detail)];
                  }
                  const quarterlyDetail = gastoMensualAggregated.value.details?.[ctx.dataIndex];
                  if (!quarterlyDetail) return baseLine;
                  return [baseLine, ...buildDetalleTooltip(quarterlyDetail)];
                },
              },
            },
          },
          interaction: { mode: 'index', intersect: false },
          scales: {
            y: {
              ticks: {
                callback: (value) => formatCurrency(value),
              },
              grid: { color: 'rgba(0,0,0,0.05)' },
            },
            x: {
              grid: { display: false },
            },
          },
        },
      });
    };

    const renderBarChart = async () => {
      const ctx = barChartRef.value?.getContext('2d');
      if (!ctx) return;
      const Chart = await loadChartJs();
      if (barChartInstance) barChartInstance.destroy();

      barChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: consultasLabels.value,
          datasets: [
            {
              label: 'Consultas',
              data: consultasData.value,
              backgroundColor: '#6366f1',
              borderRadius: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = consultasData.value.reduce((acc, v) => acc + (Number(v) || 0), 0);
                  const val = Number(ctx.parsed.y || 0);
                  const pct = total ? ((val / total) * 100).toFixed(1) : '0.0';
                  return ` ${val.toLocaleString('es-AR')} consultas (${pct}%)`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
            },
            x: {
              grid: { display: false },
            },
          },
        },
      });
    };

    const renderCharts = async () => {
      await renderLineChart();
      await renderBarChart();
    };

    watch(
      series,
      async () => {
        await nextTick();
        await renderCharts();
      },
      { deep: true }
    );

    watch(gastoView, async () => {
      await nextTick();
      await renderCharts();
    });

    watch(loading, async (isLoading) => {
      if (isLoading) return;
      await nextTick();
      await renderCharts();
    });

    onMounted(async () => {
      await loadData();
    });

    onBeforeUnmount(() => {
      destroyCharts();
    });

    return {
      loading,
      loadingSeries,
      errorMessage,
      selectedYear,
      yearOptions,
      kpiCards,
      topAfiliados,
      distRangos,
      distGenero,
      distDelegaciones,
      lineChartRef,
      barChartRef,
      formatCurrency,
      gastoView,
      hasGastoData,
      hasConsultasData,

      onYearChange,

      totales,
      totalesResumidos,
      formatNumber,
    };
  },
};
</script>
